import { List, ListItem, ListItemText, Popover } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
//import { notificationData } from './notificationData';
import Notification from './Notification';
import BellIcon from '../../SvgIcons/BellIcon';
import BellNewIcon from '../../SvgIcons/BellNewIcon';
import { useValidateLogin } from '../../../hooks/auth';
import { getMainURL } from './notification-helper';
import { useNotifications } from '../../../hooks/notifications';
import { useInView } from "react-intersection-observer";
import { getAllDataFromPages } from '../../../actions/commonActions';
import { useQueryClient } from 'react-query';
import {
    ASSET_MANAGEMENT,
    ASSETS_DETAILS,
    DOMAIN_OVERALL_DASHBOARD,
    DOMAIN_SPECIFIC_DASHBOARD,
    EASM_OVERALL_DASHBOARD,
    LIST_EASM_MONITORING,
} from '../../../hooks/utils/constants';

// ROAR-5054/ROAR-5075 — the two outcomes api-backend-provapt's sendEasmScanNotification.js
// (generalised from the success-only sender) can report for a finished scan.
const EASM_SCAN_NOTIFICATION_TYPES = ['EASM_SCAN_COMPLETED', 'EASM_SCAN_FAILED'];

const Notifications = () => {
    const [notifyEl, setNotifyEl] = useState(null);
    const open = Boolean(notifyEl);
    const [newNotification, setNewNotification] = useState(false);
    let { data } = useValidateLogin();
    const mainUrl = getMainURL(data?.user?.role);
    const {data: notificationData, fetchNextPage } = useNotifications();
    const notifications = getAllDataFromPages(notificationData?.pages, 'notifications');
    const hasUnReadNotification = notificationData?.pages[0]?.unreadNotificationCount;
    const hasNextPage = notificationData?.pages[0]?.hasNextPage;
    const queryClient = useQueryClient();
    const processedEasmScanNotificationIds = useRef(new Set());

    const handleClick = (event) => {
        setNotifyEl(notifyEl ? null : event.currentTarget);
    };

    const handleClose = () => {
        setNotifyEl(null);
    };

    const notificationIcon = newNotification ? (
        <BellNewIcon className="cursor-pointer" />
    ) : (
        <BellIcon className="cursor-pointer" />
    );

    const {inView, ref} = useInView();
    useEffect(() => {  
        if (inView) {
            fetchNextPage();
        }            
    }, [inView]);

    
    useEffect(() => {
        if (hasUnReadNotification) {
            setNewNotification(true);
        } else {
            setNewNotification(false);
        }
    }, [hasUnReadNotification])

    /**
     * ROAR-5054/ROAR-5075 (AC6) — no new socket, no new polling loop. Piggybacks on the existing
     * 5-minute `useNotifications()` poll: when a not-yet-seen EASM_SCAN_COMPLETED or
     * EASM_SCAN_FAILED row shows up, invalidate the asset-listing/EASM-dashboard/monitoring query
     * keys so "Last scan on" live-updates elsewhere without a hard reload — a failed scan still
     * writes `last_scan` (ROAR-5074's put.updateAssetDetails.js settles/releases the credit hold
     * on both outcomes). `processedEasmScanNotificationIds` stops a later re-render of the same
     * still-unread row (the poll keeps returning it until the user opens the bell) from
     * invalidating again. `ASSETS_DETAILS` is included alongside the rest for the same reason
     * `useStartEasmScan`/`useBulkStartEasmScan` (src/hooks/easm.js) already invalidate it — it's
     * what `DomainDashboard`/`SubdomainDashboard` read `easm_scan_status`/"Last scan on" from.
     * Deliberately NOT invalidating CREDITS_STATUS/CREDITS_ESTIMATE here: useCreditsStatus
     * (src/hooks/billing.js) already self-heals via its own 60s poll + refetchOnWindowFocus —
     * strictly faster than this 5-minute-polled notification could ever trigger it — and
     * useCreditsEstimate is a per-form quote (Start EASM modal, GenPT chat), not a persistent
     * display, so there's nothing for a background notification to usefully refresh there.
     */
    useEffect(() => {
        const freshEasmScanNotifications = notifications.filter(
            (notification) =>
                EASM_SCAN_NOTIFICATION_TYPES.includes(notification?.type) &&
                !notification?.seen_at &&
                !processedEasmScanNotificationIds.current.has(notification?.id)
        );
        if (!freshEasmScanNotifications.length) {
            return;
        }
        freshEasmScanNotifications.forEach((notification) => {
            processedEasmScanNotificationIds.current.add(notification?.id);
        });
        queryClient.invalidateQueries(ASSET_MANAGEMENT);
        queryClient.invalidateQueries(ASSETS_DETAILS);
        queryClient.invalidateQueries(DOMAIN_OVERALL_DASHBOARD);
        queryClient.invalidateQueries(DOMAIN_SPECIFIC_DASHBOARD);
        queryClient.invalidateQueries(EASM_OVERALL_DASHBOARD);
        queryClient.invalidateQueries(LIST_EASM_MONITORING);
    }, [notifications, queryClient])

    const notificationsSection = notifications && notifications?.length > 0 ? (
        notifications.map((notification, index) => {
            return (
                <Notification
                    key={index}
                    notification={notification}
                    onClose={handleClose}
                    mainUrl={mainUrl}
                />
            )
        })) : (
            <ListItem alignItems="flex-start"  className="!w-[530px]">
                <ListItemText className="text-center text-body" primary={"- No notifications to display -"}/>
            </ListItem>
        );

    return (
        <div className="!w-9 !min-h-9 text-center rounded-full" style={{ backgroundColor: open ? 'rgba(128, 128, 128, 0.2)' : 'transparent' }}>
            <span onClick={handleClick}>{notificationIcon}</span>
            <Popover
                open={open}
                anchorEl={notifyEl}
                id={"account-menu"}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                variant={"menu"}
                autoFocus={false}
                className={"block app-notifications"}
            >
                <div className='max-h-[450px] flex overflow-y-auto my-1'>
                    <List sx={{width: '100%', maxWidth: 530, bgcolor: 'background.paper' }} className="!mx-3 !my-2 !py-0">
                        {notificationsSection}
                    </List>
                    {hasNextPage && <div className={"h-[10px]"} ref={ref}></div>}
                </div>
            </Popover>
        </div>
    )
}

export default Notifications;